/**
 * API Client pour la gestion des taux de TVA (Tax Rates)
 * ✅ SKILL FACTURXPRO: Architecture 3 couches - API Layer
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


// Types basés sur le modèle Prisma
export interface TaxRate {
  id: string;
  tenantId: string;
  companyId: string;
  rate: number;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  isDefault: boolean;
  name: string;
}

export interface TaxRatesResponse {
  taxRates: TaxRate[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface CreateTaxRateDTO {
  rate: number;
  name: string;
  countryCode?: string;
  isActive?: boolean;
}

export interface UpdateTaxRateDTO {
  rate?: number;
  name?: string;
  countryCode?: string;
  isActive?: boolean;
}

/**
 * API Tax Rates
 */
export const taxRatesAPI = {
  /**
   * Récupérer tous les taux de TVA avec filtres
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    activeOnly?: boolean;
    countryCode?: string;
  }): Promise<TaxRatesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.activeOnly !== undefined) queryParams.append('activeOnly', params.activeOnly.toString());
    if (params?.countryCode) queryParams.append('countryCode', params.countryCode);

    const url = `${API_URL}/tax/tax-rates${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération des taux de TVA');
    }

    const apiResponse = await safeJsonParse(response);

    if (apiResponse.success && apiResponse.data) {
      return {
        taxRates: Array.isArray(apiResponse.data) ? apiResponse.data : [],
        pagination: apiResponse.meta,
      };
    }

    return {
      taxRates: Array.isArray(apiResponse) ? apiResponse : apiResponse.taxRates || [],
      pagination: apiResponse.pagination,
    };
  },

  /**
   * Récupérer un taux de TVA par ID
   */
  async getById(id: string): Promise<TaxRate> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération du taux de TVA');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Récupérer le taux de TVA par défaut
   */
  async getDefault(): Promise<TaxRate> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates/default`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération du taux de TVA par défaut');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Créer un taux de TVA
   */
  async create(data: CreateTaxRateDTO): Promise<TaxRate> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la création du taux de TVA');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Mettre à jour un taux de TVA
   */
  async update(id: string, data: UpdateTaxRateDTO): Promise<TaxRate> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la mise à jour du taux de TVA');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Supprimer un taux de TVA
   */
  async delete(id: string): Promise<void> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la suppression du taux de TVA');
    }
  },

  /**
   * Définir comme taux de TVA par défaut
   */
  async setDefault(id: string): Promise<TaxRate> {
    const response = await safeFetch(`${API_URL}/tax/tax-rates/${id}/default`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la définition du taux par défaut');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Activer/Désactiver un taux de TVA
   */
  async setActive(id: string, isActive: boolean): Promise<TaxRate> {
    return this.update(id, { isActive });
  },
};
